import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  MessageSquare,
  Send,
  RefreshCw,
  Search,
  Plus,
  User,
  Phone,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLL_INTERVAL_MS = 8000;

const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const formatMessageTime = (timestamp) => {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const WhatsAppIntegrationPage = () => {
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);

  // New conversation dialog
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [manualNumber, setManualNumber] = useState('');

  const messagesEndRef = useRef(null);
  const selectedPhoneRef = useRef(null);

  useEffect(() => {
    selectedPhoneRef.current = selectedPhone;
  }, [selectedPhone]);

  const fetchConversations = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoadingConversations(true);
    try {
      const response = await axios.get(`${API}/whatsapp/conversations`);
      const data = Array.isArray(response.data) ? response.data : [];
      data.sort((a, b) =>
        (b.last_timestamp || '').localeCompare(a.last_timestamp || '')
      );
      setConversations(data);
    } catch (error) {
      console.error('Erro ao buscar conversas:', error);
      if (showSpinner) toast.error('Erro ao carregar conversas');
    } finally {
      if (showSpinner) setLoadingConversations(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations(true);
    const interval = setInterval(() => fetchConversations(false), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/employees`, {
        params: { active_only: true },
      });
      const data = Array.isArray(response.data) ? response.data : [];
      const withPhone = data.filter((e) => e.whatsapp || e.phone);
      setEmployees(withPhone);
    } catch (error) {
      console.error('Erro ao buscar funcionários:', error);
      toast.error('Erro ao carregar funcionários');
    }
  }, []);

  const selectedConversation = conversations.find(
    (c) => c.phone_number === selectedPhone
  );

  const messages = selectedConversation?.messages || [];

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, selectedPhone]);

  const handleOpenNewDialog = (open) => {
    setNewDialogOpen(open);
    if (open) {
      fetchEmployees();
      setEmployeeSearch('');
      setManualNumber('');
    }
  };

  const sendMessage = async ({ recipientType, phoneNumber, employeeId, text }) => {
    setSending(true);
    try {
      const response = await axios.post(`${API}/whatsapp/send-admin-message`, {
        recipient_type: recipientType,
        phone_number: phoneNumber,
        employee_id: employeeId,
        message: text,
      });
      const phone = response.data?.phone_number;
      toast.success('Mensagem enviada');
      await fetchConversations(false);
      if (phone) setSelectedPhone(phone);
      return phone;
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      const detail =
        error.response?.data?.detail || 'Erro ao enviar mensagem pelo WhatsApp';
      toast.error(detail);
      return null;
    } finally {
      setSending(false);
    }
  };

  const handleSendInChat = async () => {
    const text = messageText.trim();
    if (!text || !selectedPhone) return;
    const phone = await sendMessage({
      recipientType: 'phone',
      phoneNumber: selectedPhone,
      text,
    });
    if (phone) setMessageText('');
  };

  const handleStartWithEmployee = async (employee) => {
    const text = window.prompt(
      `Mensagem para ${employee.nome || 'funcionário'}:`
    );
    if (text === null) return;
    if (!text.trim()) {
      toast.error('Digite uma mensagem');
      return;
    }
    const phone = await sendMessage({
      recipientType: 'employee',
      employeeId: employee.id,
      text: text.trim(),
    });
    if (phone) setNewDialogOpen(false);
  };

  const handleStartWithNumber = async () => {
    const number = manualNumber.trim();
    if (!number) {
      toast.error('Digite um número');
      return;
    }
    const text = window.prompt('Mensagem para ' + number + ':');
    if (text === null) return;
    if (!text.trim()) {
      toast.error('Digite uma mensagem');
      return;
    }
    const phone = await sendMessage({
      recipientType: 'phone',
      phoneNumber: number,
      text: text.trim(),
    });
    if (phone) setNewDialogOpen(false);
  };

  const filteredConversations = conversations.filter((c) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      (c.sender_name || '').toLowerCase().includes(term) ||
      (c.phone_number || '').toLowerCase().includes(term) ||
      (c.last_message || '').toLowerCase().includes(term)
    );
  });

  const filteredEmployees = employees.filter((e) => {
    if (!employeeSearch.trim()) return true;
    const term = employeeSearch.toLowerCase();
    return (
      (e.nome || '').toLowerCase().includes(term) ||
      (e.whatsapp || '').toLowerCase().includes(term) ||
      (e.phone || '').toLowerCase().includes(term)
    );
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">WhatsApp</h1>
          <p className="text-slate-600 mt-1">
            Converse com os funcionários pelo WhatsApp
          </p>
        </div>
        <Button
          onClick={() => fetchConversations(true)}
          variant="outline"
          size="sm"
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${loadingConversations ? 'animate-spin' : ''}`}
          />
          Atualizar
        </Button>
      </div>

      <div className="flex h-[calc(100vh-220px)] min-h-[480px] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        {/* Lista de conversas */}
        <div className="flex w-full max-w-xs flex-col border-r border-slate-200 md:max-w-sm">
          <div className="border-b border-slate-200 p-3 space-y-3">
            <Dialog open={newDialogOpen} onOpenChange={handleOpenNewDialog}>
              <DialogTrigger asChild>
                <Button className="w-full" size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Nova conversa
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Nova conversa</DialogTitle>
                  <DialogDescription>
                    Escolha um funcionário cadastrado ou informe um número.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Por número
                    </label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Ex: 11999999999"
                        value={manualNumber}
                        onChange={(e) => setManualNumber(e.target.value)}
                        disabled={sending}
                      />
                      <Button
                        onClick={handleStartWithNumber}
                        disabled={sending || !manualNumber.trim()}
                      >
                        <Phone className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Por funcionário
                    </label>
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <Input
                        className="pl-8"
                        placeholder="Buscar funcionário..."
                        value={employeeSearch}
                        onChange={(e) => setEmployeeSearch(e.target.value)}
                      />
                    </div>
                    <div className="max-h-60 overflow-y-auto rounded-md border border-slate-200">
                      {filteredEmployees.length === 0 ? (
                        <div className="p-4 text-center text-sm text-slate-500">
                          Nenhum funcionário com telefone encontrado
                        </div>
                      ) : (
                        filteredEmployees.map((emp) => (
                          <button
                            key={emp.id}
                            onClick={() => handleStartWithEmployee(emp)}
                            disabled={sending}
                            className="flex w-full items-center gap-3 border-b border-slate-100 p-3 text-left transition hover:bg-slate-50 disabled:opacity-50"
                          >
                            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-600">
                              {getInitials(emp.nome)}
                            </div>
                            <div className="min-w-0">
                              <div className="truncate text-sm font-medium text-slate-800">
                                {emp.nome || 'Sem nome'}
                              </div>
                              <div className="truncate text-xs text-slate-500">
                                {emp.whatsapp || emp.phone}
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                className="pl-8"
                placeholder="Buscar conversa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loadingConversations && conversations.length === 0 ? (
              <div className="p-4 text-center text-sm text-slate-500">
                Carregando conversas...
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="flex flex-col items-center gap-2 p-8 text-center text-sm text-slate-500">
                <MessageSquare className="h-8 w-8 text-slate-300" />
                <span>Nenhuma conversa ainda</span>
              </div>
            ) : (
              filteredConversations.map((conv) => {
                const isActive = conv.phone_number === selectedPhone;
                return (
                  <button
                    key={conv.phone_number}
                    onClick={() => setSelectedPhone(conv.phone_number)}
                    className={`flex w-full items-center gap-3 border-b border-slate-100 p-3 text-left transition hover:bg-slate-50 ${
                      isActive ? 'bg-slate-100' : ''
                    }`}
                  >
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 text-sm font-semibold text-emerald-700">
                      {getInitials(conv.sender_name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium text-slate-800">
                          {conv.sender_name || conv.phone_number}
                        </span>
                        <span className="flex-shrink-0 text-xs text-slate-400">
                          {formatTime(conv.last_timestamp)}
                        </span>
                      </div>
                      <div className="truncate text-xs text-slate-500">
                        {conv.direction === 'outgoing' ? 'Você: ' : ''}
                        {conv.last_message || '...'}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Painel de mensagens */}
        <div className="flex flex-1 flex-col bg-slate-50">
          {!selectedConversation ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center text-slate-400">
              <MessageSquare className="h-12 w-12" />
              <div>
                <p className="font-medium text-slate-500">
                  Selecione uma conversa
                </p>
                <p className="text-sm">
                  ou inicie uma nova conversa com um funcionário
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 border-b border-slate-200 bg-white p-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-sm font-semibold text-emerald-700">
                  {getInitials(selectedConversation.sender_name)}
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-slate-800">
                    {selectedConversation.sender_name ||
                      selectedConversation.phone_number}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-slate-500">
                    <Phone className="h-3 w-3" />
                    {selectedConversation.phone_number}
                  </div>
                </div>
              </div>

              <div className="flex-1 space-y-3 overflow-y-auto p-4">
                {messages.length === 0 ? (
                  <div className="text-center text-sm text-slate-400">
                    Nenhuma mensagem nesta conversa
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isOutgoing = msg.direction === 'outgoing';
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${
                          isOutgoing ? 'justify-end' : 'justify-start'
                        }`}
                      >
                        <div
                          className={`max-w-[75%] rounded-lg px-3 py-2 text-sm shadow-sm ${
                            isOutgoing
                              ? 'bg-emerald-500 text-white'
                              : 'bg-white text-slate-800'
                          }`}
                        >
                          <div className="whitespace-pre-wrap break-words">
                            {msg.message}
                          </div>
                          <div
                            className={`mt-1 text-right text-[10px] ${
                              isOutgoing ? 'text-emerald-100' : 'text-slate-400'
                            }`}
                          >
                            {formatMessageTime(msg.timestamp)}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="border-t border-slate-200 bg-white p-3">
                <div className="flex items-end gap-2">
                  <Textarea
                    placeholder="Digite uma mensagem..."
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendInChat();
                      }
                    }}
                    disabled={sending}
                    rows={1}
                    className="max-h-32 min-h-[40px] resize-none"
                  />
                  <Button
                    onClick={handleSendInChat}
                    disabled={sending || !messageText.trim()}
                  >
                    {sending ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppIntegrationPage;
